import { DiagnosticLike, ImportBinding, ImportStatement } from './types';

const identifierPattern = /[A-Za-z_]\w*/g;
const unusedImportMessagePattern = /\bunused\b|\bunaccessed\b/i;

export function detectNewline(text: string): string {
  return text.includes('\r\n') ? '\r\n' : '\n';
}

export function maskPythonSource(text: string): string {
  const chars = text.split('');
  let index = 0;
  let inComment = false;
  let delimiter = '';
  let tripleQuoted = false;

  while (index < chars.length) {
    const currentChar = chars[index];

    if (inComment) {
      if (currentChar !== '\n' && currentChar !== '\r') {
        chars[index] = ' ';
      } else {
        inComment = false;
      }
      index += 1;
      continue;
    }

    if (delimiter) {
      if (tripleQuoted) {
        if (text.startsWith(delimiter.repeat(3), index)) {
          index += 3;
          delimiter = '';
          tripleQuoted = false;
          continue;
        }
      } else if (currentChar === '\\') {
        chars[index] = ' ';
        index += 1;
        if (index < chars.length && chars[index] !== '\n' && chars[index] !== '\r') {
          chars[index] = ' ';
          index += 1;
        }
        continue;
      } else if (currentChar === delimiter) {
        delimiter = '';
        index += 1;
        continue;
      }

      if (currentChar !== '\n' && currentChar !== '\r') {
        chars[index] = ' ';
      }
      index += 1;
      continue;
    }

    if (currentChar === '#') {
      inComment = true;
      chars[index] = ' ';
      index += 1;
      continue;
    }

    if (currentChar === "'" || currentChar === '"') {
      delimiter = currentChar;
      if (text.startsWith(currentChar.repeat(3), index)) {
        tripleQuoted = true;
        chars[index] = ' ';
        chars[index + 1] = ' ';
        chars[index + 2] = ' ';
        index += 3;
      } else {
        tripleQuoted = false;
        chars[index] = ' ';
        index += 1;
      }
      continue;
    }

    index += 1;
  }

  return chars.join('');
}

export function getLineStarts(text: string): number[] {
  const starts = [0];
  for (let index = 0; index < text.length; index += 1) {
    if (text[index] === '\n') {
      starts.push(index + 1);
    }
  }
  return starts;
}

export function getLineEndOffset(text: string, lineStartOffset: number): number {
  let index = lineStartOffset;
  while (index < text.length && text[index] !== '\n' && text[index] !== '\r') {
    index += 1;
  }
  return index;
}

function getLineIndentation(line: string): number {
  return line.length - line.trimStart().length;
}

function isSimpleImportLine(maskedLine: string): boolean {
  const trimmed = maskedLine.trim();
  if (!trimmed.startsWith('import ') && !trimmed.startsWith('from ')) {
    return false;
  }

  return !trimmed.includes('(') && !trimmed.includes('\\');
}

function parseBindingList(line: string, bindingText: string, lineStartOffset: number): ImportBinding[] {
  const bindings: ImportBinding[] = [];
  let searchFrom = 0;

  for (const rawItem of bindingText.replace(/[()]/g, '').split(',')) {
    const trimmedItem = rawItem.trim();
    if (!trimmedItem) {
      continue;
    }

    const bindingMatch = trimmedItem.match(/^([A-Za-z_][\w.]*|\*)(?:\s+as\s+([A-Za-z_]\w*))?$/);
    if (!bindingMatch) {
      continue;
    }

    const relativeStart = line.indexOf(trimmedItem, searchFrom);
    if (relativeStart === -1) {
      continue;
    }

    searchFrom = relativeStart + trimmedItem.length;
    bindings.push({
      name: bindingMatch[1],
      alias: bindingMatch[2],
      startOffset: lineStartOffset + relativeStart,
      endOffset: lineStartOffset + relativeStart + trimmedItem.length,
    });
  }

  return bindings;
}

function parseParenthesizedFromImport(
  lines: string[],
  maskedLines: string[],
  lineStarts: number[],
  startLine: number,
  trimmedMaskedLine: string
): { statement: ImportStatement; endLine: number } | null {
  const fromMatch = trimmedMaskedLine.match(/^from\s+([.\w]+)\s+import\s*\($/);
  if (!fromMatch) {
    return null;
  }

  let endLine = startLine + 1;
  while (endLine < lines.length && (maskedLines[endLine] ?? '').trim() !== ')') {
    endLine += 1;
  }

  if (endLine >= lines.length) {
    return null;
  }

  const startOffset = lineStarts[startLine];
  const endOffset = lineStarts[endLine] + lines[endLine].length;
  const raw = lines.slice(startLine, endLine + 1).join('\n');
  const bindingText = lines.slice(startLine + 1, endLine).join('\n');
  const bindings = parseBindingList(raw, bindingText, startOffset);
  if (bindings.length === 0) {
    return null;
  }

  return {
    endLine,
    statement: {
      kind: 'from',
      module: fromMatch[1],
      bindings,
      startLine,
      endLine,
      startOffset,
      endOffset,
      isFuture: fromMatch[1] === '__future__',
      hasWildcard: bindings.some((binding) => binding.name === '*'),
      raw,
    },
  };
}

export function parseTopLevelImports(text: string): { statements: ImportStatement[]; hasComplexImports: boolean } {
  const maskedText = maskPythonSource(text);
  const lines = text.split(/\r?\n/);
  const maskedLines = maskedText.split(/\r?\n/);
  const lineStarts = getLineStarts(text);
  const statements: ImportStatement[] = [];
  let hasComplexImports = false;

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex];
    const maskedLine = maskedLines[lineIndex] ?? '';
    const trimmedMaskedLine = maskedLine.trim();

    if (!trimmedMaskedLine) {
      continue;
    }

    if (getLineIndentation(maskedLine) > 0) {
      continue;
    }

    if (!trimmedMaskedLine.startsWith('import ') && !trimmedMaskedLine.startsWith('from ')) {
      continue;
    }

    if (!isSimpleImportLine(maskedLine)) {
      const parenthesizedFromImport = parseParenthesizedFromImport(lines, maskedLines, lineStarts, lineIndex, trimmedMaskedLine);
      if (parenthesizedFromImport) {
        statements.push(parenthesizedFromImport.statement);
        lineIndex = parenthesizedFromImport.endLine;
        continue;
      }

      hasComplexImports = true;
      continue;
    }

    const lineStartOffset = lineStarts[lineIndex];
    const lineEndOffset = lineStartOffset + line.length;

    if (trimmedMaskedLine.startsWith('import ')) {
      const bindingText = trimmedMaskedLine.slice('import '.length);
      const bindings = parseBindingList(line, bindingText, lineStartOffset);
      if (bindings.length > 0) {
        statements.push({
          kind: 'import',
          module: '',
          bindings,
          startLine: lineIndex,
          endLine: lineIndex,
          startOffset: lineStartOffset,
          endOffset: lineEndOffset,
          isFuture: false,
          hasWildcard: false,
          raw: line,
        });
      }
      continue;
    }

    const fromMatch = trimmedMaskedLine.match(/^from\s+([.\w]+)\s+import\s+(.+)$/);
    if (!fromMatch) {
      hasComplexImports = true;
      continue;
    }

    const bindings = parseBindingList(line, fromMatch[2], lineStartOffset);
    if (bindings.length === 0) {
      continue;
    }

    statements.push({
      kind: 'from',
      module: fromMatch[1],
      bindings,
      startLine: lineIndex,
      endLine: lineIndex,
      startOffset: lineStartOffset,
      endOffset: lineEndOffset,
      isFuture: fromMatch[1] === '__future__',
      hasWildcard: bindings.some((binding) => binding.name === '*'),
      raw: line,
    });
  }

  return { statements, hasComplexImports };
}

function extractFunctionParameters(parameterList: string): string[] {
  return parameterList
    .split(',')
    .map((parameter) => parameter.trim())
    .map((parameter) => parameter.replace(/^[*]+/, ''))
    .map((parameter) => parameter.split(':')[0]?.trim() ?? '')
    .map((parameter) => parameter.split('=')[0]?.trim() ?? '')
    .filter((parameter) => /^[A-Za-z_]\w*$/.test(parameter));
}

export function collectDefinedNames(text: string): Set<string> {
  const maskedText = maskPythonSource(text);
  const definedNames = new Set<string>();

  for (const match of maskedText.matchAll(/(?:^|\n)[ \t]*(?:async\s+def|def|class)\s+([A-Za-z_]\w*)/g)) {
    definedNames.add(match[1]);
  }

  for (const match of maskedText.matchAll(/(?:^|\n)[ \t]*([A-Za-z_]\w*)\s*(?::[^=\n]+)?=/g)) {
    definedNames.add(match[1]);
  }

  for (const match of maskedText.matchAll(/(?:^|\n)[ \t]*(?:for|async\s+for)\s+([A-Za-z_]\w*)\s+in\b/g)) {
    definedNames.add(match[1]);
  }

  for (const match of maskedText.matchAll(/(?:^|\n)[ \t]*with\b[^\n]*\bas\s+([A-Za-z_]\w*)/g)) {
    definedNames.add(match[1]);
  }

  for (const match of maskedText.matchAll(/(?:^|\n)[ \t]*except\b[^\n]*\bas\s+([A-Za-z_]\w*)/g)) {
    definedNames.add(match[1]);
  }

  for (const match of maskedText.matchAll(/(?:^|\n)[ \t]*(?:async\s+def|def)\s+[A-Za-z_]\w*\s*\(([^)]*)\)/g)) {
    for (const parameter of extractFunctionParameters(match[1])) {
      definedNames.add(parameter);
    }
  }

  for (const match of maskedText.matchAll(/lambda\s+([^:]+):/g)) {
    for (const parameter of extractFunctionParameters(match[1])) {
      definedNames.add(parameter);
    }
  }

  return definedNames;
}

function isOffsetInsideImport(offset: number, imports: ImportStatement[]): boolean {
  return imports.some((importStatement) => offset >= importStatement.startOffset && offset <= importStatement.endOffset);
}

function isStringPrefixIdentifier(text: string, startOffset: number, endOffset: number): boolean {
  const prefix = text.slice(startOffset, endOffset);
  if (!/^[rRuUbBfF]{1,3}$/.test(prefix)) {
    return false;
  }

  return !isIdentifierCharacter(text[startOffset - 1]) && (text[endOffset] === '"' || text[endOffset] === "'");
}

function skipStringLiteral(text: string, quoteOffset: number): number {
  const quote = text[quoteOffset];
  const quoteLength = text.startsWith(quote.repeat(3), quoteOffset) ? 3 : 1;
  let offset = quoteOffset + quoteLength;

  while (offset < text.length) {
    if (quoteLength === 3 && text.startsWith(quote.repeat(3), offset)) {
      return offset + quoteLength;
    }

    if (quoteLength === 1) {
      if (text[offset] === '\\') {
        offset += 2;
        continue;
      }

      if (text[offset] === quote) {
        return offset + quoteLength;
      }

      if (text[offset] === '\n' || text[offset] === '\r') {
        return offset;
      }
    }

    offset += 1;
  }

  return offset;
}

function findFStringExpressionEnd(text: string, startOffset: number): number | null {
  let offset = startOffset;
  let braceDepth = 0;

  while (offset < text.length) {
    const currentChar = text[offset];

    if (currentChar === '"' || currentChar === "'") {
      offset = skipStringLiteral(text, offset);
      continue;
    }

    if (currentChar === '{') {
      braceDepth += 1;
      offset += 1;
      continue;
    }

    if (currentChar === '}') {
      if (braceDepth === 0) {
        return offset;
      }

      braceDepth -= 1;
    }

    offset += 1;
  }

  return null;
}

function collectIdentifiersFromFStringExpression(text: string, startOffset: number, endOffset: number, identifiers: Set<string>) {
  const expression = text.slice(startOffset, endOffset);
  const maskedExpression = maskPythonSource(expression);

  for (const match of maskedExpression.matchAll(identifierPattern)) {
    identifiers.add(match[0]);
  }
}

function collectFStringExpressionIdentifiers(text: string, imports: ImportStatement[]): Set<string> {
  const identifiers = new Set<string>();
  let offset = 0;

  while (offset < text.length) {
    const currentChar = text[offset];

    if (currentChar === '#') {
      while (offset < text.length && text[offset] !== '\n' && text[offset] !== '\r') {
        offset += 1;
      }
      continue;
    }

    if (currentChar === '"' || currentChar === "'") {
      offset = skipStringLiteral(text, offset);
      continue;
    }

    if (/[rRuUbBfF]/.test(currentChar) && !isIdentifierCharacter(text[offset - 1])) {
      let prefixEnd = offset;
      while (prefixEnd < text.length && /[rRuUbBfF]/.test(text[prefixEnd])) {
        prefixEnd += 1;
      }

      const quote = text[prefixEnd];
      if ((quote === '"' || quote === "'") && /[fF]/.test(text.slice(offset, prefixEnd))) {
        const stringEndOffset = skipStringLiteral(text, prefixEnd);
        let expressionOffset = prefixEnd + (text.startsWith(quote.repeat(3), prefixEnd) ? 3 : 1);

        while (expressionOffset < stringEndOffset) {
          if (text.startsWith('{{', expressionOffset) || text.startsWith('}}', expressionOffset)) {
            expressionOffset += 2;
            continue;
          }

          if (text[expressionOffset] !== '{') {
            expressionOffset += 1;
            continue;
          }

          const expressionEndOffset = findFStringExpressionEnd(text, expressionOffset + 1);
          if (expressionEndOffset === null || expressionEndOffset > stringEndOffset || isOffsetInsideImport(expressionOffset, imports)) {
            break;
          }

          collectIdentifiersFromFStringExpression(text, expressionOffset + 1, expressionEndOffset, identifiers);
          expressionOffset = expressionEndOffset + 1;
        }

        offset = stringEndOffset;
        continue;
      }
    }

    offset += 1;
  }

  return identifiers;
}

export function collectUsedIdentifiers(text: string, imports: ImportStatement[]): Set<string> {
  const maskedText = maskPythonSource(text);
  const usedIdentifiers = new Set<string>();

  for (const match of maskedText.matchAll(identifierPattern)) {
    const offset = match.index ?? -1;
    if (offset === -1 || isOffsetInsideImport(offset, imports) || isStringPrefixIdentifier(text, offset, offset + match[0].length)) {
      continue;
    }
    usedIdentifiers.add(match[0]);
  }

  for (const identifier of collectFStringExpressionIdentifiers(text, imports)) {
    usedIdentifiers.add(identifier);
  }

  return usedIdentifiers;
}

function isIdentifierCharacter(character: string | undefined): boolean {
  return character !== undefined && /[A-Za-z0-9_]/.test(character);
}

export function extractIdentifierAtOffset(text: string, offset: number): { identifier: string; startOffset: number; endOffset: number } | null {
  if (offset < 0 || offset >= text.length) {
    return null;
  }

  let startOffset = offset;
  let endOffset = offset;

  while (startOffset > 0 && isIdentifierCharacter(text[startOffset - 1])) {
    startOffset -= 1;
  }

  while (endOffset < text.length && isIdentifierCharacter(text[endOffset])) {
    endOffset += 1;
  }

  const identifier = text.slice(startOffset, endOffset);
  if (!/^[A-Za-z_]\w*$/.test(identifier)) {
    return null;
  }

  return {
    identifier,
    startOffset,
    endOffset,
  };
}

export function isNamespaceUsage(text: string, endOffset: number): boolean {
  const maskedText = maskPythonSource(text);
  let offset = endOffset;
  while (offset < maskedText.length && /\s/.test(maskedText[offset])) {
    offset += 1;
  }
  return maskedText[offset] === '.';
}

export function normalizeDiagnosticCode(code: unknown): string | undefined {
  if (code === undefined || code === null) {
    return undefined;
  }

  if (typeof code === 'string' || typeof code === 'number') {
    return String(code);
  }

  if (typeof code === 'object' && 'value' in code) {
    const value = (code as { value?: unknown }).value;
    if (typeof value === 'string' || typeof value === 'number') {
      return String(value);
    }
  }

  return undefined;
}

export function isMissingImportDiagnostic(diagnostic: DiagnosticLike): boolean {
  const code = normalizeDiagnosticCode(diagnostic.code)?.toLowerCase();
  if (code?.includes('undefined') || code?.includes('unbound')) {
    return true;
  }

  return /\bis not defined\b|\bundefined\b|\bunbound\b/i.test(diagnostic.message);
}

export function isUnusedImportDiagnostic(diagnostic: DiagnosticLike): boolean {
  const code = normalizeDiagnosticCode(diagnostic.code)?.toLowerCase();
  if (code?.includes('unused') || code?.includes('unaccessed')) {
    return true;
  }

  if (diagnostic.tags?.includes(1) && unusedImportMessagePattern.test(diagnostic.message)) {
    return true;
  }

  return /\bunused import\b|\bunaccessed import\b/i.test(diagnostic.message);
}

export function extractDiagnosticIdentifier(text: string, diagnostic: DiagnosticLike): string | null {
  const byOffset = extractIdentifierAtOffset(text, diagnostic.startOffset);
  if (byOffset) {
    return byOffset.identifier;
  }

  const quotedMatch = diagnostic.message.match(/["'`]{1}([A-Za-z_]\w*)["'`]{1}/);
  if (quotedMatch) {
    return quotedMatch[1];
  }

  const wordMatch = diagnostic.message.match(/\b([A-Za-z_]\w*)\b/);
  return wordMatch?.[1] ?? null;
}

export function getImportBindingSymbol(statement: ImportStatement, binding: ImportBinding): string {
  if (binding.alias) {
    return binding.alias;
  }

  if (statement.kind === 'import') {
    return binding.name.split('.')[0];
  }

  return binding.name;
}

function getDocstringEndLine(text: string): number | null {
  const lines = text.split(/\r?\n/);
  let lineIndex = 0;

  while (lineIndex < lines.length) {
    const trimmedLine = lines[lineIndex].trim();
    if (!trimmedLine) {
      lineIndex += 1;
      continue;
    }

    if (lineIndex === 0 && trimmedLine.startsWith('#!')) {
      lineIndex += 1;
      continue;
    }

    if (trimmedLine.startsWith('#')) {
      lineIndex += 1;
      continue;
    }

    // Python allows prefixed string literals as module-level docstrings (r"""...""",
    // u"""...""", etc.). Match an optional prefix so imports aren't injected above them.
    const quoteMatch = trimmedLine.match(/^([rRbBuUfF]{0,2})("""|'''|"|')/);
    if (!quoteMatch) {
      return null;
    }

    const prefix = quoteMatch[1];
    const quote = quoteMatch[2];
    const isTripleQuoted = quote.length === 3;
    if (isTripleQuoted) {
      const joinedText = lines.slice(lineIndex).join('\n');
      const closingIndex = joinedText.indexOf(quote, prefix.length + quote.length);
      if (closingIndex === -1) {
        return lines.length - 1;
      }
      const consumedText = joinedText.slice(0, closingIndex + quote.length);
      return lineIndex + consumedText.split('\n').length - 1;
    }

    return lineIndex;
  }

  return null;
}

export function findLeadingImportRegion(
  text: string,
  topLevelImports: ImportStatement[]
): {
  firstImport: ImportStatement | null;
  lastImport: ImportStatement | null;
  firstExecutableLine: number;
  insertionOffset: number;
  needsTrailingBlankLine: boolean;
} {
  const lines = text.split(/\r?\n/);
  const maskedLines = maskPythonSource(text).split(/\r?\n/);
  const lineStarts = getLineStarts(text);
  const docstringEndLine = getDocstringEndLine(text);
  let scanLine = docstringEndLine === null ? 0 : docstringEndLine + 1;
  let firstImport: ImportStatement | null = null;
  let lastImport: ImportStatement | null = null;
  let firstExecutableLine = lines.length;

  while (scanLine < lines.length) {
    const trimmedMaskedLine = (maskedLines[scanLine] ?? '').trim();
    if (!trimmedMaskedLine || trimmedMaskedLine.startsWith('#')) {
      scanLine += 1;
      continue;
    }

    const importStatement = topLevelImports.find((statement) => statement.startLine === scanLine);
    if (!importStatement) {
      firstExecutableLine = scanLine;
      break;
    }

    if (!firstImport) {
      firstImport = importStatement;
    }
    lastImport = importStatement;
    scanLine = importStatement.endLine + 1;
  }

  if (firstExecutableLine === lines.length && scanLine < lines.length) {
    firstExecutableLine = scanLine;
  }

  const insertionOffset =
    lastImport !== null ? lastImport.endOffset : firstExecutableLine < lines.length ? lineStarts[firstExecutableLine] : text.length;

  return {
    firstImport,
    lastImport,
    firstExecutableLine,
    insertionOffset,
    needsTrailingBlankLine: lastImport !== null ? firstExecutableLine <= lastImport.endLine + 1 : firstExecutableLine < lines.length,
  };
}
